import { MainMetaMap } from "@tsTypes/meta-schema";
import { Question } from "@tsTypes/question-schema";
import { QuestionTotalsBySubTopic } from "@tsTypes/test-builder-schema";
import { calculateQuestionTotalMarks } from "../katex/render";
import { strapiData } from "../strapi/strapiApi";
import { checkIfMultipleChoiceQuestion } from "./helpers";

export async function getQuestionTotalsBySubTopic(
  topicDocumentIds: string[],
  mainMetaMap: MainMetaMap
) {
  const subjectQuestionsByTopic = await Promise.all(
    topicDocumentIds.map(async (topicDocumentId) => {
      const subTopicDocumentIds = Object.entries(mainMetaMap)
        .filter(([key, value]) => {
          if (
            "topicDocumentId" in value &&
            value.topicDocumentId === topicDocumentId
          ) {
            return true;
          }
        })
        .map(([key, value]) => key);

      return {
        topicDocumentId,
        subTopicDocumentIds,
        topicQuestions: await getTopicQuestionsForTestBuilder(
          subTopicDocumentIds
        ),
      };
    })
  );

  return convertQuestionsByTopicToQuestionTotalsBySubTopic(
    subjectQuestionsByTopic
  );
}

export function convertQuestionsByTopicToQuestionTotalsBySubTopic(
  subjectQuestionsByTopic: {
    topicDocumentId: string;
    subTopicDocumentIds: string[];
    topicQuestions: Question[];
  }[]
) {
  return subjectQuestionsByTopic.reduce(
    (acc: QuestionTotalsBySubTopic, topicMeta) => {
      topicMeta.topicQuestions.forEach((q) => {
        const questionRelevantSubTopic = q.RelatedSubTopics.find((s) =>
          topicMeta.subTopicDocumentIds.includes(s.documentId)
        )?.documentId!;
        if (!acc[questionRelevantSubTopic]) {
          acc[questionRelevantSubTopic] = {
            Easy: {
              MC: { numQuestions: 0, numMarks: 0 },
              LA: { numQuestions: 0, numMarks: 0 },
            },
            Medium: {
              MC: { numQuestions: 0, numMarks: 0 },
              LA: { numQuestions: 0, numMarks: 0 },
            },
            Hard: {
              MC: { numQuestions: 0, numMarks: 0 },
              LA: { numQuestions: 0, numMarks: 0 },
            },
          };
        }
        const questionType = checkIfMultipleChoiceQuestion(q) ? "MC" : "LA";
        // @ts-ignore
        acc[questionRelevantSubTopic][q.Difficulty][questionType]
          .numQuestions++;
        // @ts-ignore

        acc[questionRelevantSubTopic][q.Difficulty][questionType].numMarks +=
          calculateQuestionTotalMarks(q);
      });
      return acc;
    },
    {}
  );
}

async function getTopicQuestionsForTestBuilder(
  topicSubTopicDocumentIds: string[]
) {
  return (await strapiData(
    process.env.STRAPI_API_TOKEN!,
    "questions",
    buildTopicQuestionsForTestBuilderQuery(topicSubTopicDocumentIds)
  )) as Question[];
}

function buildTopicQuestionsForTestBuilderQuery(
  topicSubTopicDocumentIds: string[]
) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["Difficulty"],
      populate: {
        QuestionStyle: {
          populate: "*", // Over the top but strapi crashing when trying to sepecify
        },
        RelatedSubTopics: {
          fields: ["documentId"],
        },
      },
      filters: {
        RelatedSubTopics: {
          documentId: {
            $in: topicSubTopicDocumentIds,
          },
        },
        // Include questions not assigned to a topic test, or those assigned with a source other than "Test questions" (Test bank)
        $or: [
          {
            RelatedTopicTests: {
              $null: true,
            },
          },
          {
            RelatedTopicTests: {
              QuestionSource: {
                $ne: "Test questions",
              },
            },
          },
        ],
      },
    },
    {
      encodeValuesOnly: true,
    }
  );
  return query;
}
